import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API } from './config';
import { useAuth } from './App';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './components/ui/tooltip';
import {
  Users,
  TrendingUp,
  DollarSign,
  Heart,
  MessageCircle,
  Dumbbell,
  Apple,
  Eye,
  Crown,
  BarChart3,
  BookOpen,
  ShoppingCart,
  ArrowRight,
  Loader2,
  Pin,
  Calendar,
  Repeat,
  Zap,
  Info,
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const PERIODS = [
  { value: 'all', label: 'All Time' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '7d', label: 'Last 7 Days' },
];

function formatNumber(n) {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return String(n);
}

function formatCurrency(n) {
  return '$' + Number(n || 0).toFixed(2);
}

// ─── Stat Card ─────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sublabel, color = 'blue', isCurrency = false, onClick, tooltip }) => {
  const colorMap = {
    blue:   'from-blue-500 to-cyan-500',
    green:  'from-emerald-500 to-teal-500',
    purple: 'from-violet-500 to-purple-500',
    amber:  'from-amber-500 to-orange-500',
    rose:   'from-rose-500 to-pink-500',
    indigo: 'from-indigo-500 to-blue-500',
    teal:   'from-teal-500 to-cyan-500',
    slate:  'from-slate-600 to-slate-500',
  };
  const bgMap = {
    blue:   'bg-blue-50 text-blue-600',
    green:  'bg-emerald-50 text-emerald-600',
    purple: 'bg-violet-50 text-violet-600',
    amber:  'bg-amber-50 text-amber-600',
    rose:   'bg-rose-50 text-rose-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    teal:   'bg-teal-50 text-teal-600',
    slate:  'bg-slate-50 text-slate-600',
  };
  const isClickable = typeof onClick === 'function';
  const body = (
    <Card
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      className={`relative overflow-hidden border-0 shadow-sm transition-all ${isClickable ? 'hover:shadow-md hover:-translate-y-0.5 hover:ring-2 hover:ring-blue-400/40 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400/60' : 'hover:shadow-md'}`}
      data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${colorMap[color] || colorMap.blue} opacity-[0.04]`} />
      <CardContent className="p-5 relative">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold text-gray-900">
              {isCurrency ? formatCurrency(value) : formatNumber(value)}
            </p>
            {sublabel && <p className="text-xs text-gray-500">{sublabel}</p>}
          </div>
          <div className={`p-2.5 rounded-xl ${bgMap[color] || bgMap.blue} ${isClickable ? 'group-hover:scale-105 transition-transform' : ''}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
        {isClickable && (
          <ArrowRight className="absolute bottom-3 right-3 h-3.5 w-3.5 text-gray-300" aria-hidden="true" />
        )}
      </CardContent>
    </Card>
  );
  if (tooltip) {
    return (
      <TooltipProvider delayDuration={200}>
        <UITooltip>
          <TooltipTrigger asChild>
            <div className="group">{body}</div>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs"><p className="text-xs">{tooltip}</p></TooltipContent>
        </UITooltip>
      </TooltipProvider>
    );
  }
  return <div className="group">{body}</div>;
};

// ─── Revenue Breakdown ─────────────────────────────────────
const RevenueBreakdown = React.forwardRef(({ data, totalRevenue }, ref) => {
  if (!data) return null;
  const recurring = data.subscriptions || 0;
  const oneTime = data.one_time_total ?? ((data.workout_plans || 0) + (data.meal_plans || 0) + (data.tips || 0));
  const items = [
    { label: 'Subscriptions', value: recurring, color: '#6366f1', badge: 'Recurring' },
    { label: 'Workout Plans', value: data.workout_plans || 0, color: '#10b981', badge: 'One-time' },
    { label: 'Meal Plans', value: data.meal_plans || 0, color: '#f59e0b', badge: 'One-time' },
    { label: 'Tips', value: data.tips || 0, color: '#ec4899', badge: 'One-time' },
  ];
  const total = totalRevenue ?? items.reduce((s, i) => s + i.value, 0);
  const hasRecurring = (data.mrr || 0) > 0 || (data.active_paying_subscribers || 0) > 0;
  if (total === 0 && !hasRecurring) {
    return (
      <Card ref={ref} className="border-0 shadow-sm" data-testid="revenue-breakdown-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-emerald-600" />
            <span>Revenue Breakdown</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <DollarSign className="h-10 w-10 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No revenue recorded yet</p>
            <p className="text-xs text-gray-400 mt-1">Revenue will appear here as sales come in</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card ref={ref} className="border-0 shadow-sm" data-testid="revenue-breakdown-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-emerald-600" />
          <span>Revenue Breakdown</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Recurring vs One-time top section */}
        <div className="grid grid-cols-2 gap-2 pb-3 border-b border-gray-100">
          <div className="p-2 rounded-lg bg-indigo-50/60 border border-indigo-100" data-testid="revenue-group-recurring">
            <p className="text-[10px] font-semibold text-indigo-700 uppercase tracking-wider flex items-center gap-1"><Repeat className="h-3 w-3" /> Recurring</p>
            <p className="text-lg font-bold text-gray-900 mt-0.5">{formatCurrency(recurring)}</p>
            <p className="text-[10px] text-gray-500">{data.active_paying_subscribers || 0} paying subscribers</p>
          </div>
          <div className="p-2 rounded-lg bg-emerald-50/60 border border-emerald-100" data-testid="revenue-group-one-time">
            <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider flex items-center gap-1"><Zap className="h-3 w-3" /> One-time</p>
            <p className="text-lg font-bold text-gray-900 mt-0.5">{formatCurrency(oneTime)}</p>
            <p className="text-[10px] text-gray-500">plans · tips · one-offs</p>
          </div>
        </div>
        {items.map((it) => (
          <div key={it.label} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: it.color }} />
              <span className="text-sm text-gray-700">{it.label}</span>
              <Badge variant="outline" className="text-[9px] py-0 px-1.5 text-gray-500 border-gray-200">{it.badge}</Badge>
            </div>
            <span className="text-sm font-semibold text-gray-900">{formatCurrency(it.value)}</span>
          </div>
        ))}
        <div className="pt-2 border-t">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900">Total</span>
            <span className="text-sm font-bold text-gray-900">{formatCurrency(total)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});
RevenueBreakdown.displayName = 'RevenueBreakdown';

// ─── Top Content ───────────────────────────────────────────
const TopContent = ({ items }) => {
  if (!items || items.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-blue-600" />
            <span>Top Performing Content</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <BarChart3 className="h-10 w-10 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No sales data yet</p>
            <p className="text-xs text-gray-400 mt-1">Your best-selling products will show here</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-blue-600" />
          <span>Top Performing Content</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50/80">
            <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
              <p className="text-xs text-gray-500">{item.type} &middot; {item.sales} sale{item.sales !== 1 ? 's' : ''}</p>
            </div>
            <span className="text-sm font-semibold text-emerald-600">{formatCurrency(item.revenue)}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// ─── Engagement Chart ──────────────────────────────────────
const EngagementChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Heart className="h-4 w-4 text-rose-500" />
            <span>Engagement Over Time</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Heart className="h-10 w-10 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Not enough data for a chart yet</p>
            <p className="text-xs text-gray-400 mt-1">Post content and engage with your audience to see trends</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Heart className="h-4 w-4 text-rose-500" />
          <span>Engagement Over Time</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gradLikes" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradComments" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                labelStyle={{ fontWeight: 600 }}
              />
              <Area type="monotone" dataKey="likes" stroke="#f43f5e" fill="url(#gradLikes)" strokeWidth={2} name="Likes" />
              <Area type="monotone" dataKey="comments" stroke="#6366f1" fill="url(#gradComments)" strokeWidth={2} name="Comments" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Follower Growth Chart ─────────────────────────────────
const FollowerGrowthChart = ({ data }) => {

  if (!data || data.length === 0) return null;
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Users className="h-4 w-4 text-blue-600" />
          <span>Follower Growth</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                labelStyle={{ fontWeight: 600 }}
              />
              <Bar dataKey="followers" fill="#3b82f6" radius={[4, 4, 0, 0]} name="New Followers" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Recent Activity ───────────────────────────────────────
const ACTIVITY_ICONS = {
  new_follower: Users,
  new_subscriber: Crown,
  purchase: ShoppingCart,
  bookmark: Pin,
  like: Heart,
  comment: MessageCircle,
};
const ACTIVITY_COLORS = {
  new_follower: 'bg-blue-100 text-blue-600',
  new_subscriber: 'bg-purple-100 text-purple-600',
  purchase: 'bg-emerald-100 text-emerald-600',
  bookmark: 'bg-amber-100 text-amber-600',
  like: 'bg-rose-100 text-rose-600',
  comment: 'bg-indigo-100 text-indigo-600',
};

const RecentActivity = ({ items }) => {
  if (!items || items.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-gray-600" />
            <span>Recent Activity</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <BookOpen className="h-10 w-10 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No recent activity</p>
            <p className="text-xs text-gray-400 mt-1">Activity from followers, subscribers, and buyers will appear here</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-gray-600" />
          <span>Recent Activity</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 max-h-72 overflow-y-auto">
        {items.map((a, i) => {
          const Ic = ACTIVITY_ICONS[a.type] || BookOpen;
          const cls = ACTIVITY_COLORS[a.type] || 'bg-gray-100 text-gray-600';
          return (
            <div key={i} className="flex items-center gap-3 py-2 border-b last:border-0 border-gray-50">
              <div className={`p-1.5 rounded-lg ${cls} flex-shrink-0`}>
                <Ic className="h-3.5 w-3.5" />
              </div>
              <p className="text-sm text-gray-700 flex-1 min-w-0 truncate">{a.description}</p>
              <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">{a.time_ago}</span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

// ─── Empty Dashboard ───────────────────────────────────────
const EmptyDashboard = () => (
  <div className="text-center py-16">
    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-500/20">
      <BarChart3 className="h-10 w-10 text-white" />
    </div>
    <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Your Analytics</h2>
    <p className="text-gray-600 max-w-md mx-auto mb-6">
      Start creating content and engaging with your audience. Your performance metrics will appear here as your creator journey grows.
    </p>
    <div className="flex flex-wrap justify-center gap-3">
      <Badge variant="outline" className="px-3 py-1.5 text-sm">Create Posts</Badge>
      <Badge variant="outline" className="px-3 py-1.5 text-sm">Upload Workout Plans</Badge>
      <Badge variant="outline" className="px-3 py-1.5 text-sm">Share Meal Plans</Badge>
    </div>
  </div>
);

// ─── Main Analytics Component ──────────────────────────────
export const CreatorAnalytics = () => {
  const [period, setPeriod] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const revenueSectionRef = useRef(null);
  const engagementSectionRef = useRef(null);
  const scrollTo = (ref) => { if (ref?.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/creator/analytics?period=${period}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="analytics-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-600 font-medium">Loading analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16" data-testid="analytics-error">
        <BarChart3 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Unable to load analytics</h3>
        <p className="text-sm text-gray-500 mb-4">{error}</p>
        <Button variant="outline" onClick={fetchAnalytics}>Try Again</Button>
      </div>
    );
  }

  if (!data) return null;

  const ov = data.overview;
  const isNewCreator = ov.total_followers === 0 && ov.total_posts === 0 && ov.total_workout_plans === 0 && ov.total_meal_plans === 0;
  if (isNewCreator) return <EmptyDashboard />;

  const periodLabel = PERIODS.find(p => p.value === period)?.label || 'All Time';

  return (
    <div className="space-y-6" data-testid="creator-analytics-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            Creator Analytics
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">Track your growth, revenue, and engagement</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40" data-testid="analytics-period-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map(p => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Period context badge */}
      {period !== 'all' && data.period_stats && (
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className="text-xs">
            Showing: {periodLabel}
          </Badge>
          {data.period_stats.new_followers > 0 && (
            <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs">+{data.period_stats.new_followers} new followers</Badge>
          )}
          {data.period_stats.new_subscribers > 0 && (
            <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-xs">+{data.period_stats.new_subscribers} new subscribers</Badge>
          )}
          {data.period_stats.period_revenue > 0 && (
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">+{formatCurrency(data.period_stats.period_revenue)} revenue</Badge>
          )}
        </div>
      )}

      {/* Revenue Intelligence (MRR / ARR / One-time) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-revenue-intelligence">
        <StatCard
          icon={DollarSign}
          label="Total Revenue"
          value={ov.total_revenue}
          isCurrency
          color="green"
          onClick={() => scrollTo(revenueSectionRef)}
          sublabel="all-time cumulative"
          tooltip="All completed and paid transactions from your subscriptions, plans, and tips."
        />
        <StatCard
          icon={Repeat}
          label="MRR"
          value={data.revenue_breakdown?.mrr || 0}
          isCurrency
          color="indigo"
          onClick={() => navigate('/my-subscribers')}
          sublabel={`${data.revenue_breakdown?.active_paying_subscribers || 0} paying subs`}
          tooltip="Monthly Recurring Revenue = sum of monthly-equivalent price across all active paying subscriptions. Annual plans contribute price/12."
        />
        <StatCard
          icon={Calendar}
          label="ARR"
          value={data.revenue_breakdown?.arr || 0}
          isCurrency
          color="purple"
          onClick={() => navigate('/my-subscribers')}
          sublabel="MRR × 12 projection"
          tooltip="Annual Recurring Revenue — projected yearly run rate if current MRR holds steady."
        />
        <StatCard
          icon={Zap}
          label="One-Time Payments"
          value={data.revenue_breakdown?.one_time_total || 0}
          isCurrency
          color="amber"
          onClick={() => scrollTo(revenueSectionRef)}
          sublabel="plans · tips · one-offs"
          tooltip="Total non-recurring revenue from workout plans, meal plans, tips, and other one-time purchases."
        />
      </div>

      {/* Stat Cards Grid — all clickable routing to the most relevant existing surface */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="analytics-stat-cards">
        <StatCard icon={Users} label="Followers" value={ov.total_followers} color="blue"
          onClick={() => navigate('/connections')} tooltip="People following you. Click to manage." />
        <StatCard icon={Crown} label="Subscribers" value={ov.active_subscribers} sublabel={`${ov.total_subscribers} total`} color="purple"
          onClick={() => navigate('/my-subscribers')} tooltip="Active paying subscribers. Click to view the full subscriber list." />
        <StatCard icon={Eye} label="Profile Views" value={ov.profile_views} color="teal"
          onClick={() => scrollTo(engagementSectionRef)} tooltip="Unique views on your profile. Click to see the engagement timeline showing when views happen." />
        <StatCard icon={BookOpen} label="Posts" value={ov.total_posts} color="indigo"
          onClick={() => user?.id && navigate(`/profile/${user.id}?tab=posts`)} tooltip="All posts you've created. Click to open your Posts tab." />
        <StatCard icon={Dumbbell} label="Workout Plans" value={ov.total_workout_plans} color="amber"
          onClick={() => user?.id && navigate(`/profile/${user.id}?tab=workouts`)} tooltip="Workout plans you've published. Click to open your Workouts tab." />
        <StatCard icon={Apple} label="Meal Plans" value={ov.total_meal_plans} color="rose"
          onClick={() => user?.id && navigate(`/profile/${user.id}?tab=meals`)} tooltip="Meal plans you've published. Click to open your Meals tab." />
        <StatCard icon={Heart} label="Engagement" value={ov.total_likes + ov.total_comments} sublabel={`${ov.total_likes} likes, ${ov.total_comments} comments`} color="rose"
          onClick={() => scrollTo(engagementSectionRef)} tooltip="Total likes + comments across your posts. Click to see the engagement chart." />
        <StatCard icon={Pin} label="Bookmarks" value={ov.total_bookmarks} sublabel="saves on your content" color="amber"
          onClick={() => user?.id && navigate(`/profile/${user.id}?tab=workouts`)} tooltip="Times someone saved your plans. Click to review the content people are saving." />
        <StatCard icon={ShoppingCart} label="Purchases" value={ov.total_purchases} color="green"
          onClick={() => navigate('/purchase-history')} tooltip="Paid purchases of your plans and content. Click to open Purchase History." />
      </div>

      {/* Charts + Panels */}
      <div ref={engagementSectionRef} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <EngagementChart data={data.engagement_timeline} />
        <FollowerGrowthChart data={data.follower_growth} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <RevenueBreakdown ref={revenueSectionRef} data={data.revenue_breakdown} totalRevenue={ov.total_revenue} />
        </div>
        <div className="lg:col-span-1">
          <TopContent items={data.top_content} />
        </div>
        <div className="lg:col-span-1">
          <RecentActivity items={data.recent_activity} />
        </div>
      </div>
    </div>
  );
};
